import { FC } from 'react'

import { Box, Skeleton } from '@mui/material'

import { SectionCard } from '../SectionCard/SectionCard'

interface TableSkeletonProps {
  /** Number of placeholder rows. Defaults to 8 — roughly fills the dialog body. */
  rows?: number
  /** Number of placeholder columns (drives column widths). Defaults to 5. */
  columns?: number
  /**
   * When `true`, the skeleton renders without the outer `SectionCard` so it
   * can be embedded inside a parent that already provides one.
   */
  bare?: boolean
  /** Optional title placeholder width (px). Defaults to 220. */
  titleWidth?: number
}

/**
 * Layout-matched skeleton for the listing/detail dialogs that render a
 * `SectionCard` + `GRTable`. The placeholder mirrors the header row
 * (title + right-aligned action) and a configurable number of rows so the
 * dialog paper does not visibly resize when real data arrives.
 */
export const TableSkeleton: FC<TableSkeletonProps> = ({ rows = 8, columns = 5, bare = false, titleWidth = 220 }) => {
  const rowSkeleton = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" width={i === 0 ? '32%' : '16%'} height={18} />
        ))}
      </Box>
      {/* Body rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.25,
            borderBottom: rowIdx === rows - 1 ? 0 : 1,
            borderColor: 'divider',
          }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              variant={colIdx === 0 ? 'rectangular' : 'text'}
              width={colIdx === 0 ? '32%' : '16%'}
              height={colIdx === 0 ? 36 : 16}
              sx={colIdx === 0 ? { borderRadius: 1 } : undefined}
            />
          ))}
        </Box>
      ))}
    </Box>
  )

  if (bare) return rowSkeleton

  return (
    <SectionCard title={<Skeleton variant="text" width={titleWidth} height={28} />} disableBodyPadding>
      {rowSkeleton}
    </SectionCard>
  )
}
